/**
 * Camera-based QR scanner using html5-qrcode.
 * Used as a bottom-sheet from the Arena Scanner Launcher (top-bar QR icon).
 *
 * Behavior:
 *   - Asks for camera permission, opens rear camera.
 *   - On scan, decodes URL. If it's a /checkin/{gym_id}/{token} link or
 *     /arena/{code} page (FitFrik), navigate to it directly.
 *   - On non-FitFrik URL or unrecognised format → toast error and keep scanning.
 *   - Manual code entry fallback button.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { X, Keyboard } from "lucide-react";
import { toast } from "sonner";

export default function QRScanner({ open, onClose }) {
  const navigate = useNavigate();
  const [manualCode, setManualCode] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const containerId = "qr-scanner-container";

  useEffect(() => {
    if (!open) return;
    let html5 = null;
    setError(null);
    const start = async () => {
      try {
        html5 = new Html5Qrcode(containerId);
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => handleDecoded(decoded),
          () => {} // ignore intermediate errors
        );
      } catch (e) {
        setError(e?.message || "Camera permission denied");
      }
    };
    const t = setTimeout(start, 100);
    return () => {
      clearTimeout(t);
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current.clear?.();
        scannerRef.current = null;
      }
    };
  }, [open]);

  const handleDecoded = (text) => {
    try {
      const url = new URL(text);
      // Accept FitFrik URLs (production OR preview OR localhost)
      const path = url.pathname;
      const m1 = path.match(/^\/checkin\/([^/]+)\/([^/]+)\/?$/);
      const m2 = path.match(/^\/arena\/([A-Za-z0-9-]+)\/?$/);
      if (m1) {
        if (scannerRef.current) scannerRef.current.stop().catch(() => {});
        navigate(`/checkin/${m1[1]}/${m1[2]}`);
        onClose();
        return;
      }
      if (m2) {
        if (scannerRef.current) scannerRef.current.stop().catch(() => {});
        navigate(`/arena/${m2[1]}`);
        onClose();
        return;
      }
      toast.error("Not a FitFrik QR code");
    } catch {
      // Treat as plain gym code (e.g., user printed just "TESTGYM" as QR)
      if (/^[A-Z0-9]{4,12}$/.test(text)) {
        if (scannerRef.current) scannerRef.current.stop().catch(() => {});
        navigate(`/arena/${text}`);
        onClose();
        return;
      }
      toast.error("Unrecognised QR content");
    }
  };

  const submitManual = () => {
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    navigate(`/arena/${code}`);
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/85" data-testid="qr-scanner-modal">
      <header style={{ position: "absolute", top: 0, left: 0, right: 0, padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", color: "white", zIndex: 2 }}>
        <span style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800 }}>Scan gym QR</span>
        <button onClick={onClose} aria-label="Close" data-testid="qr-close" style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.10)", color: "white", border: "none", display: "grid", placeItems: "center" }}>
          <X size={18} />
        </button>
      </header>

      {!showManual && (
        <div style={{ position: "absolute", inset: 0 }}>
          <div id={containerId} style={{ width: "100%", height: "100%", background: "black" }} />
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
            <div style={{ width: 240, height: 240, border: "2px solid #F5C518", borderRadius: 16, boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)" }} />
          </div>
          {error && (
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 120, padding: 16, background: "rgba(165,33,33,0.92)", color: "white", textAlign: "center", margin: "0 24px", borderRadius: 12 }}>
              <div style={{ fontFamily: "Barlow Condensed", fontSize: 16, fontWeight: 800 }}>Camera unavailable</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>{error}</div>
            </div>
          )}
        </div>
      )}

      {showManual && (
        <div style={{ position: "absolute", inset: 0, padding: 24, display: "grid", placeItems: "center", background: "#0C0C0B", zIndex: 5 }}>
          <div style={{ width: "100%", maxWidth: 360, background: "white", borderRadius: 20, padding: 24 }}>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Enter gym code</div>
            <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>Find the 6-char code on your gym poster.</p>
            <input value={manualCode} onChange={(e) => setManualCode(e.target.value.slice(0, 12).toUpperCase())} placeholder="e.g. IRONHAUS" autoFocus data-testid="qr-manual-input"
              style={{ width: "100%", height: 48, marginTop: 14, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "monospace", fontSize: 18, letterSpacing: "0.2em", textAlign: "center", outline: "none" }} />
            <button onClick={submitManual} disabled={!manualCode.trim()} data-testid="qr-manual-submit"
              style={{ width: "100%", height: 48, marginTop: 14, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 14, fontWeight: 800 }}>
              Find gym
            </button>
          </div>
        </div>
      )}

      <footer style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "16px 24px env(safe-area-inset-bottom, 16px)", background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 100%)" }}>
        <button onClick={() => setShowManual((v) => !v)} data-testid="qr-toggle-manual"
          style={{ width: "100%", height: 44, background: "rgba(255,255,255,0.10)", color: "white", border: "1px solid rgba(255,255,255,0.20)", borderRadius: 100, fontFamily: "Barlow", fontSize: 13, fontWeight: 700, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 6, backdropFilter: "blur(10px)" }}>
          <Keyboard size={14} /> {showManual ? "Use camera" : "Enter code manually"}
        </button>
      </footer>
    </div>
  );
}
